import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  createGroupedOptionsForTypeaheadSelect,
  groupEntitiesByCategory,
} from '@/modules/entities/utils/groupEntitiesByCategory';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useLiabilitiesIssuersAndRecipientsQuery } from '@/modules/liabilities/LiabilityModal/graphql/LiabilityModal.generated';
import { EntityKind, EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

/** Entity kinds that can hold insurance policies */
const POLICY_OWNER_ENTITY_KINDS: EntityKind[] = [
  EntityKind.InsurancePersonalAccount,
  EntityKind.IlitTrust,
  EntityKind.SlatTrust,
  EntityKind.CrtTrust,
  EntityKind.CltTrust,
  EntityKind.IrrevocableTrust,
  EntityKind.RevocableTrust,
  EntityKind.DonorAdvisedFund,
  EntityKind.PrivateFoundation,
  EntityKind.CcorpBusinessEntity,
  EntityKind.ScorpBusinessEntity,
  EntityKind.LlcBusinessEntity,
  EntityKind.LpBusinessEntity,
  EntityKind.GpBusinessEntity,
];

interface UseOwnerEntityOptionsReturn {
  options: TypeaheadSelectInputOption<string>[];
  entityKindById: Record<string, EntityKind>;
  entityStageById: Record<string, EntityStage>;
  subtypeIdByEntityId: Record<string, string>;
}

export function useOwnerEntityOptions(): UseOwnerEntityOptionsReturn {
  const { householdId } = useHouseholdDetailsContext();

  const { data } = useLiabilitiesIssuersAndRecipientsQuery({
    variables: {
      where: { id: householdId },
    },
  });

  const household = getNodes(data?.households)[0];

  return useMemo(() => {
    if (!household) {
      return {
        options: [],
        entityKindById: {},
        entityStageById: {},
        subtypeIdByEntityId: {},
      };
    }

    const entityRecipients = getNodes(household.entities);

    const filteredEntities = entityRecipients.filter(
      (e) =>
        (e.stage === EntityStage.Active ||
          e.stage === EntityStage.PreCreated) &&
        POLICY_OWNER_ENTITY_KINDS.includes(e.kind)
    );

    const entityKindById: Record<string, EntityKind> = {};
    const entityStageById: Record<string, EntityStage> = {};
    const subtypeIdByEntityId: Record<string, string> = {};
    for (const entity of filteredEntities) {
      entityKindById[entity.id] = entity.kind;
      entityStageById[entity.id] = entity.stage;
      subtypeIdByEntityId[entity.id] = entity.subtype.id;
    }

    const groupedEntities = groupEntitiesByCategory(filteredEntities);
    const options = createGroupedOptionsForTypeaheadSelect(groupedEntities);

    return {
      options,
      entityKindById,
      entityStageById,
      subtypeIdByEntityId,
    };
  }, [household]);
}
